import { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  BookOpenIcon,
  AcademicCapIcon,
  PuzzlePieceIcon,
  PencilSquareIcon,
  ArrowPathRoundedSquareIcon,
  ClockIcon,
  PlusIcon,
  TrashIcon,
  EyeIcon,
  ChevronDownIcon,
  PencilIcon,
} from "@heroicons/react/24/outline";
import BasicInfoStep from "../shared/BasicInfoStep";
import LearningOutcomesStep from "../shared/LessonPlannerLearningOutcomesStep";
import CrossProgramStep from "../shared/CrossProgramStep";
import LearningMethodsStep from "../shared/LearningMethodsStep";
import AdditionalInstructionsStep from "../shared/AdditionalInstructionsStep";
import LessonPlanSummary from "../shared/LessonPlanSummary";
import LessonPlanOutput from "../shared/LessonPlanOutput";
import {
  useCreateExtraCard,
  useGetLessonPlannerV2,
  useSaveLessonPlannerResult,
  createLessonPlannerV2,
} from "@packages/api/madlenAPI";
import { LessonPlanOutput as LessonPlanOutputType } from "../../types/LessonPlan";
import { Text } from "@core/text";
import { useQueryClient } from "@tanstack/react-query";
import {
  AdditionalArrangementsDto,
  CreateExtraCardRequestDtoSectionName,
  GetTopicsCourseType,
  LearningMethodsDto,
  LearningOutcomeDto,
  LessonPlannerV2ResponseDto,
  LessonPlannerV2ResponseDtoSection,
  LessonPlannerV2ResponseDtoStatus,
  SectionContentDto,
  CreateLessonPlannerV2RequestDto,
  CreateLessonPlannerV2RequestDtoCourseType,
  CreateLessonPlannerV2RequestDtoPlanType,
} from "@packages/api/madlenAPI.schemas";
import { Button } from "@core/button";
import { useToast } from "@packages/contexts/ToastContext";
import { CourseData } from "@packages/constant/CourseData";
import { trackEvent } from "@packages/hooks/useAnalytics";
import LoadingContainer from "@packages/components/Loading";
import { getEnvironment } from "@packages/utils";

const segmentNamesInOrder = [
  "introduction",
  "presentation",
  "guidedPractice",
  "independentPractice",
  "assessment",
  "closure",
];

const sectionIcons = {
  introduction: BookOpenIcon,
  presentation: AcademicCapIcon,
  guidedPractice: PuzzlePieceIcon,
  independentPractice: PencilSquareIcon,
  assessment: ClockIcon,
  closure: EyeIcon,
};

const sectionColors = {
  introduction: {
    primary: "from-blue-500 to-blue-600",
    light: "from-blue-50 to-blue-100",
    dark: "from-blue-900/20 to-blue-800/20",
    text: "text-blue-700 dark:text-blue-300",
    bg: "bg-blue-100 dark:bg-blue-900",
    border: "border-blue-200 dark:border-blue-700",
    hover: "hover:bg-blue-50 dark:hover:bg-blue-900/30",
    button: "bg-blue-600 hover:bg-blue-700",
    ring: "focus:ring-blue-500/50",
  },
  presentation: {
    primary: "from-purple-500 to-purple-600",
    light: "from-purple-50 to-purple-100",
    dark: "from-purple-900/20 to-purple-800/20",
    text: "text-purple-700 dark:text-purple-300",
    bg: "bg-purple-100 dark:bg-purple-900",
    border: "border-purple-200 dark:border-purple-700",
    hover: "hover:bg-purple-50 dark:hover:bg-purple-900/30",
    button: "bg-purple-600 hover:bg-purple-700",
    ring: "focus:ring-purple-500/50",
  },
  guidedPractice: {
    primary: "from-emerald-500 to-emerald-600",
    light: "from-emerald-50 to-emerald-100",
    dark: "from-emerald-900/20 to-emerald-800/20",
    text: "text-emerald-700 dark:text-emerald-300",
    bg: "bg-emerald-100 dark:bg-emerald-900",
    border: "border-emerald-200 dark:border-emerald-700",
    hover: "hover:bg-emerald-50 dark:hover:bg-emerald-900/30",
    button: "bg-emerald-600 hover:bg-emerald-700",
    ring: "focus:ring-emerald-500/50",
  },
  independentPractice: {
    primary: "from-orange-500 to-orange-600",
    light: "from-orange-50 to-orange-100",
    dark: "from-orange-900/20 to-orange-800/20",
    text: "text-orange-700 dark:text-orange-300",
    bg: "bg-orange-100 dark:bg-orange-900",
    border: "border-orange-200 dark:border-orange-700",
    hover: "hover:bg-orange-50 dark:hover:bg-orange-900/30",
    button: "bg-orange-600 hover:bg-orange-700",
    ring: "focus:ring-orange-500/50",
  },
  assessment: {
    primary: "from-rose-500 to-rose-600",
    light: "from-rose-50 to-rose-100",
    dark: "from-red-900/20 to-red-800/20",
    text: "text-rose-700 dark:text-rose-300",
    bg: "bg-rose-100 dark:bg-rose-900",
    border: "border-rose-200 dark:border-rose-700",
    hover: "hover:bg-rose-50 dark:hover:bg-rose-900/30",
    button: "bg-rose-600 hover:bg-rose-700",
    ring: "focus:ring-rose-500/50",
  },
  closure: {
    primary: "from-indigo-500 to-indigo-600",
    light: "from-indigo-50 to-indigo-100",
    dark: "from-indigo-900/20 to-indigo-800/20",
    text: "text-indigo-700 dark:text-indigo-300",
    bg: "bg-indigo-100 dark:bg-indigo-900",
    border: "border-indigo-200 dark:border-indigo-700",
    hover: "hover:bg-indigo-50 dark:hover:bg-indigo-900/30",
    button: "bg-indigo-600 hover:bg-indigo-700",
    ring: "focus:ring-indigo-500/50",
  },
};

export interface LessonPlanFormProps {
  type: "daily" | "maarif";
  planId?: number;
}

interface BaseFormData {
  courseType: string;
  grade: number;
  learningOutcomes: LearningOutcomeDto[];
  learningMethods: LearningMethodsDto;
  additionalInstructions: string;
  additionalArrangements: AdditionalArrangementsDto;
  sections: {
    [key: string]: (SectionContentDto & { id: string })[];
  };
}

interface MaarifFormData extends BaseFormData {
  crossProgramComponents: {
    selected: string[];
    customInput: string;
  };
}

type FormData = BaseFormData | MaarifFormData;

interface SectionItem {
  id: string;
  name: string;
  details: string;
  duration: number;
  segmentName: string;
}

const LessonPlanForm: React.FC<LessonPlanFormProps> = ({ type, planId }) => {
  const { t, i18n } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [showSummary, setShowSummary] = useState(false);
  const [generatedPlan, setGeneratedPlan] =
    useState<LessonPlanOutputType | null>(null);
  const [isFormComplete, setIsFormComplete] = useState(false);
  const [isLoadingPlan, setIsLoadingPlan] = useState(false);
  const [lessonPlanId, setLessonPlanId] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [expandedFinalCards, setExpandedFinalCards] = useState<string[]>([]);
  const [showLoadingThenSummary, setShowLoadingThenSummary] = useState(false);

  const [editingCard, setEditingCard] = useState<string | null>(null);
  const [editingData, setEditingData] = useState<{
    name: string;
    details: string;
    duration: number;
  } | null>(null);

  const { mutateAsync: saveLessonPlan } = useSaveLessonPlannerResult();
  const queryClient = useQueryClient();

  const [planData, setPlanData] = useState<FormData>(() => {
    const baseData: BaseFormData = {
      courseType: "",
      grade: 0,
      learningOutcomes: [],
      learningMethods: {
        selected: [],
        teachingStyle: 50,
      },
      additionalInstructions: "",
      additionalArrangements: {
        differentiation: [],
        funActivities: [],
        technology: [],
        ai: [],
      },
      sections: {
        introduction: [],
        presentation: [],
        guidedPractice: [],
        independentPractice: [],
        assessment: [],
        closure: [],
      },
    };

    if (type === "maarif") {
      return {
        ...baseData,
        crossProgramComponents: {
          selected: [],
          customInput: "",
        },
      };
    }

    return baseData;
  });

  const { addToast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [finalPlan, setFinalPlan] = useState<SectionItem[]>([]);
  const [isGeneratingExtraCard, setIsGeneratingExtraCard] = useState<
    string | null
  >(null);
  const { trackEvent: track } = trackEvent();

  const finalPlanContainerRef = useRef<HTMLDivElement>(null);
  const finalPlanItemRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [lastAddedItemId, setLastAddedItemId] = useState<string | null>(null);

  // Loading steps for lesson plan generation
  const loadingSteps = useMemo(() => {
    if (i18n.language === "tr") {
      return [
        "Öğrenme çıktıları analiz ediliyor...",
        "Ders yapısı oluşturuluyor...",
        "Etkinlikler üretiliyor...",
        "İçerik düzenleniyor...",
        "Ders planı tamamlanıyor...",
      ];
    }
    if (i18n.language === "fr") {
      return [
        "Analyse des résultats d'apprentissage...",
        "Création de la structure du cours...",
        "Génération d'activités...",
        "Organisation du contenu...",
        "Finalisation du plan de cours...",
      ];
    }
    if (i18n.language === "de") {
      return [
        "Lernergebnisse werden analysiert...",
        "Unterrichtsstruktur wird erstellt...",
        "Aktivitäten werden generiert...",
        "Inhalte werden organisiert...",
        "Unterrichtsplan wird fertiggestellt...",
      ];
    }
    return [
      "Analyzing learning outcomes...",
      "Creating lesson structure...",
      "Generating activities...",
      "Organizing content...",
      "Finalizing lesson plan...",
    ];
  }, [i18n.language]);

  const sendGenerateEvent = () => {
    track(
      {
        category: "LessonPlanner",
        action: "lessonPlanner.generate.click",
        label: "LessonPlanner generate click",
        nonInteraction: false,
      },
      {
        location: "/lesson-planning-tools/daily-plan/new",
        element: "generate-button",
      },
    );
  };

  const sendGenerateExtraCardEvent = () => {
    track(
      {
        category: "LessonPlanner",
        action: "lessonPlanner.generate.extraCard.click",
        label: "LessonPlanner generate extra card click",
        nonInteraction: false,
      },
      {
        location: "/lesson-planning-tools/daily-plan/new",
        element: "generate-extra-card-button",
      },
    );
  };

  const steps = useMemo(() => {
    const baseSteps = [
      {
        id: "basicInfo",
        title: t("dailyLessonPlan.steps.basicInfo"),
        icon: BookOpenIcon,
      },
      {
        id: "outcomes",
        title: t("dailyLessonPlan.steps.outcomes"),
        icon: AcademicCapIcon,
      },
    ];

    if (type === "maarif") {
      baseSteps.push({
        id: "crossProgram",
        title: t("dailyLessonPlan.steps.crossProgram"),
        icon: ArrowPathRoundedSquareIcon,
      });
    }

    baseSteps.push(
      {
        id: "learningMethods",
        title: t("dailyLessonPlan.steps.learningMethods"),
        icon: PuzzlePieceIcon,
      },
      {
        id: "additional",
        title: t("dailyLessonPlan.steps.additional"),
        icon: PencilSquareIcon,
      },
    );

    return baseSteps;
  }, [t, type]);

  // Initialize form with existing plan if planId is provided
  useEffect(() => {
    if (planId) {
      setLessonPlanId(planId.toString());
      setIsLoadingPlan(true);
      setIsFormComplete(true);
      setShowSummary(false);
    }
  }, [planId]);

  useEffect(() => {
    if (lastAddedItemId && finalPlanContainerRef.current) {
      const itemElement = finalPlanItemRefs.current[lastAddedItemId];
      if (itemElement) {
        const container = finalPlanContainerRef.current;
        const itemTop = itemElement.offsetTop - container.offsetTop;
        const itemBottom = itemTop + itemElement.offsetHeight;

        const containerScrollTop = container.scrollTop;
        const containerVisibleHeight = container.offsetHeight;

        if (
          itemTop < containerScrollTop ||
          itemBottom > containerScrollTop + containerVisibleHeight
        ) {
          container.scrollTo({
            top:
              itemElement.offsetTop -
              container.offsetTop -
              containerVisibleHeight / 2 +
              itemElement.offsetHeight / 2,
            behavior: "smooth",
          });
        }
      }
      setLastAddedItemId(null); // Reset after scrolling
    }
  }, [lastAddedItemId, finalPlan]);

  const {
    data,
    queryKey: getLessonPlannerQueryKey,
    refetch,
  } = useGetLessonPlannerV2(Number(lessonPlanId), {
    query: {
      enabled: !!lessonPlanId,
      refetchInterval: (query) => {
        if (
          query.state.data &&
          query.state.data.sections &&
          query.state.data.sections["introduction"] &&
          query.state.data.sections["introduction"].content &&
          query.state.data.sections["introduction"].content.length > 0 &&
          query.state.data.status === "READY"
        ) {
          return false;
        }
        return 5000;
      },
    },
  });

  const { mutateAsync: createExtraCard } = useCreateExtraCard({
    mutation: {
      onSuccess: () => {
        queryClient.setQueryData(getLessonPlannerQueryKey, (data) => {
          return {
            ...data,
            status: "PROCESSING" as LessonPlannerV2ResponseDtoStatus,
          };
        });
        refetch();
      },
    },
  });

  useEffect(() => {
    if (data && data.status === "READY") {
      let resultSections: SectionItem[] = [];

      if (data.result?.sections) {
        resultSections = segmentNamesInOrder.flatMap((segmentName) => {
          const section = (
            data.result?.sections as LessonPlannerV2ResponseDtoSection
          )?.[segmentName as keyof LessonPlannerV2ResponseDtoSection];
          if (!section?.content?.length) return [];

          return section.content.map((item: SectionContentDto) => ({
            id: Math.random().toString(),
            name:
              item.name ||
              segmentName.charAt(0).toUpperCase() + segmentName.slice(1),
            details: item.details || "",
            duration: Number(item.duration) || 0,
            segmentName: segmentName,
          }));
        });

        setFinalPlan(resultSections);
      }

      setPlanData((prev) => {
        const baseUpdatedData = {
          ...prev,
          courseType: data.courseType || prev.courseType,
          grade: data.grade || prev.grade,
          learningOutcomes: prev.learningOutcomes,
          learningMethods: prev.learningMethods,
          additionalInstructions:
            data?.result?.additionalInstructions || prev.additionalInstructions,
          additionalArrangements:
            data?.result?.additionalArrangements || prev.additionalArrangements,
          sections: Object.fromEntries(
            segmentNamesInOrder.map((sectionName) => {
              const section =
                data.sections?.[sectionName as keyof typeof data.sections];
              const sectionItems =
                section?.content.map((c) => ({
                  id: Math.random().toString(),
                  name:
                    c.name ||
                    sectionName.charAt(0).toUpperCase() + sectionName.slice(1),
                  details: c.details || "",
                  duration: Number(c?.duration) || 0,
                })) || [];

              const filteredItems = sectionItems.filter(
                (item) =>
                  !resultSections.some(
                    (resultItem) =>
                      resultItem.segmentName === sectionName &&
                      resultItem.name === item.name &&
                      resultItem.details === item.details,
                  ),
              );

              return [sectionName, filteredItems];
            }),
          ),
        };

        if (type === "maarif") {
          return {
            ...baseUpdatedData,
            crossProgramComponents: {
              selected: [],
              customInput: "",
            },
          } as MaarifFormData;
        }

        return baseUpdatedData as BaseFormData;
      });

      if (
        data.sections &&
        data.sections["introduction"] &&
        data.sections["introduction"].content &&
        data.sections["introduction"].content.length > 0
      ) {
        if (isGeneratingExtraCard) {
          addToast({
            duration: 5000,
            title: t("lessonPlanner.cardsGenerated"),
            description: t("lessonPlanner.cardsGeneratedDescription", {
              section: t(
                `dailyLessonPlan.output.sections.${isGeneratingExtraCard.toLowerCase()}`,
              ),
            }),
            variant: "success",
          });
          setIsGeneratingExtraCard(null);
        }

        // Complete loading and go directly to main interface
        if (showLoadingThenSummary) {
          setShowLoadingThenSummary(false);
        }
        setIsLoadingPlan(false);
      }
    }
  }, [data, type]);

  const handleSubmit = async () => {
    if (currentStep === steps.length - 1) {
      // Start creation immediately instead of showing summary
      setShowLoadingThenSummary(true);
      // Scroll to top when loading starts
      window.scrollTo({ top: 0, behavior: "smooth" });
      await handleSummaryConfirm();
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleSummaryConfirm = async () => {
    setShowSummary(false);
    setIsFormComplete(true);

    // Call the actual summary confirm logic from LessonPlanSummary
    const response = await handleSummaryConfirmAction();
    if (response && response.id) {
      setIsLoadingPlan(true);
      setLessonPlanId(response.id.toString());
    }
  };

  const handleSummaryConfirmAction = async () => {
    try {
      sendGenerateEvent();

      const hasOutcomes =
        type === "maarif"
          ? CourseData[planData.courseType as keyof typeof CourseData]
              ?.hasMaarifOutcomes
          : CourseData[planData.courseType as keyof typeof CourseData]
              ?.hasOutcomes;

      // Validate course type
      if (
        !Object.values(CreateLessonPlannerV2RequestDtoCourseType).includes(
          planData.courseType as CreateLessonPlannerV2RequestDtoCourseType,
        )
      ) {
        throw new Error(`Invalid course type: ${planData.courseType}`);
      }

      // Calculate total duration from segments
      const totalDuration = Object.values(planData.sections).reduce(
        (total, activities) => {
          return (
            total +
            activities.reduce(
              (sum: number, activity: { duration: number }) =>
                sum + activity.duration,
              0,
            )
          );
        },
        0,
      );

      // Ensure all required fields have values
      const learningMethodsSelected = planData.learningMethods.selected?.length
        ? planData.learningMethods.selected
        : ["experimental"]; // Default value if empty
      const learningMethodsTeachingStyle =
        planData.learningMethods.teachingStyle ?? 0;

      const nameDate = new Date().toLocaleDateString(i18n.language, {
        day: "numeric",
        month: "numeric",
        year: "numeric",
      });

      const name =
        planData.grade === -3
          ? t("courseType.MONTH_36_48") + ` - ${nameDate}`
          : planData.grade === -2
            ? t("courseType.MONTH_48_60") + ` - ${nameDate}`
            : t(
                `courseType.${CourseData[
                  planData.courseType as keyof typeof CourseData
                ]?.name(planData.grade ?? 8)}`,
              ) +
              ` - ${t("dailyLessonPlan.grade", { grade: planData.grade })} - ${nameDate}`;

      const requestData = {
        additionalArrangements: planData.additionalArrangements,
        additionalInstructions: planData.additionalInstructions || "",
        classDuration: totalDuration || 40,
        courseType:
          planData.courseType as CreateLessonPlannerV2RequestDtoCourseType,
        environment: getEnvironment(),
        grade: planData.grade,
        keyChallenges: isMaarif(planData)
          ? planData.crossProgramComponents?.customInput ||
            "No specific challenges"
          : "No specific challenges",
        learningMethods: {
          selected: learningMethodsSelected,
          teachingStyle: learningMethodsTeachingStyle,
        },
        learningObjectives: planData.learningOutcomes.map(
          (outcome) => outcome.text,
        ),
        learningOutcomes: planData.learningOutcomes,
        name: name,
        planType: (type === "daily"
          ? "DAILY"
          : "MAARIF") as CreateLessonPlannerV2RequestDtoPlanType,
      } as CreateLessonPlannerV2RequestDto;

      const response = await createLessonPlannerV2(requestData);
      return response;
    } catch (error) {
      console.error("Error generating lesson plan:", error);
      addToast({
        title: t("errors.lessonPlanGeneration"),
        description: t("errors.tryAgain"),
        variant: "error",
        duration: 5000,
      });
      throw error;
    }
  };

  const handleSummaryEdit = () => {
    setShowSummary(false);
    setCurrentStep(0);
  };

  const handleAddToFinalPlan = (sectionName: string, item: any) => {
    const newItem: SectionItem = {
      ...item,
      segmentName: sectionName,
    };

    // Find the correct position to insert based on section order
    const newFinalPlan = [...finalPlan];
    let insertIndex = 0;

    for (let i = 0; i < newFinalPlan.length; i++) {
      if (
        segmentNamesInOrder.indexOf(newFinalPlan[i].segmentName) >
        segmentNamesInOrder.indexOf(sectionName)
      ) {
        break;
      }
      insertIndex = i + 1;
    }

    newFinalPlan.splice(insertIndex, 0, newItem);
    setFinalPlan(newFinalPlan);
    setLastAddedItemId(newItem.id);

    // Remove from section
    setPlanData((prev) => ({
      ...prev,
      sections: {
        ...prev.sections,
        [sectionName]: prev.sections[sectionName].filter(
          (i) => i.id !== item.id,
        ),
      },
    }));
  };

  const handleRemoveFromFinalPlan = (item: SectionItem) => {
    setFinalPlan((prev) => prev.filter((i) => i.id !== item.id));

    // Add back to section
    setPlanData((prev) => ({
      ...prev,
      sections: {
        ...prev.sections,
        [item.segmentName]: [
          ...prev.sections[item.segmentName],
          {
            id: item.id,
            name: item.name,
            details: item.details,
            duration: item.duration,
          },
        ],
      },
    }));
  };

  const handleGenerateExtraCard = async (sectionName: string) => {
    setIsGeneratingExtraCard(sectionName);
    sendGenerateExtraCardEvent();
    try {
      const existingActivities = data?.sections?.[
        sectionName as keyof typeof data.sections
      ]?.content?.map((item) => ({
        name: item.name,
        details: item.details,
        duration: item.duration,
      }));
      if (existingActivities) {
        await createExtraCard({
          id: Number(lessonPlanId),
          data: {
            sectionName: sectionName as CreateExtraCardRequestDtoSectionName,
            existingActivities,
          },
        });
      }
    } catch (error) {
      console.error("Error creating extra card:", error);
      setIsGeneratingExtraCard(null);
    }
  };

  const handleEditCard = (sectionName: string, item: any) => {
    setEditingCard(`${sectionName}-${item.id}`);
    setEditingData({
      name: item.name,
      details: item.details,
      duration: item.duration,
    });
  };

  const handleCancelEdit = () => {
    setEditingCard(null);
    setEditingData(null);
  };

  const handleSaveEdit = (sectionName: string, itemId: string) => {
    if (!editingData) return;

    setPlanData((prev) => ({
      ...prev,
      sections: {
        ...prev.sections,
        [sectionName]: prev.sections[sectionName].map((item) =>
          item.id === itemId
            ? {
                ...item,
                name: editingData.name,
                details: editingData.details,
                duration: editingData.duration,
              }
            : item,
        ),
      },
    }));

    setEditingCard(null);
    setEditingData(null);
  };

  const handleEditFinalCard = (item: SectionItem) => {
    setEditingCard(`final-${item.id}`);
    setEditingData({
      name: item.name,
      details: item.details,
      duration: item.duration,
    });
  };

  const handleSaveFinalEdit = (itemId: string) => {
    if (!editingData) return;

    setFinalPlan((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? {
              ...item,
              name: editingData.name,
              details: editingData.details,
              duration: editingData.duration,
            }
          : item,
      ),
    );

    setEditingCard(null);
    setEditingData(null);
  };

  const handleSavePlan = async () => {
    setIsSaving(true);
    try {
      const sectionTitles = {
        assessment: "Assessment",
        closure: "Closure",
        guidedPractice: "Guided Practice",
        independentPractice: "Independent Practice",
        introduction: "Introduction",
        presentation: "Presentation",
      };
      const sections = Object.fromEntries(
        Object.entries(sectionTitles).map(([sectionName, title]) => {
          const sectionItems = finalPlan.filter(
            (item) => item.segmentName === sectionName,
          );
          return [
            sectionName,
            {
              title,
              duration: sectionItems
                .reduce((total, item) => total + item.duration, 0)
                .toString(),
              content: sectionItems.map((item) => ({
                name: item.name,
                details: item.details,
                duration: item.duration,
              })),
            },
          ];
        }),
      );

      const transformedPlan: LessonPlanOutputType = {
        lesson: planData.courseType,
        gradeLevel: planData.grade,
        ...planData,
        sections,
        crossProgramComponents: isMaarif(planData)
          ? planData.crossProgramComponents.selected // This is string[]
          : [], // Default to empty string[]
      };

      await saveLessonPlan({
        id: Number(lessonPlanId),
        data: {
          additionalInstructions: planData.additionalInstructions,
          additionalArrangements: planData.additionalArrangements, // Use from planData
          sections,
        },
      });
      return transformedPlan;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      console.error("Error generating plan:", errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleGeneratePlan = async () => {
    setIsGenerating(true);
    try {
      const transformedPlan = await handleSavePlan();
      if (transformedPlan) {
        setGeneratedPlan(transformedPlan);
      }
    } catch (error) {
      console.error("Error generating plan:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const toggleSection = (sectionName: string) => {
    setExpandedSections((prev) =>
      prev.includes(sectionName)
        ? prev.filter((s) => s !== sectionName)
        : [...prev, sectionName],
    );
  };

  const toggleFinalCard = (cardId: string) => {
    setExpandedFinalCards((prev) =>
      prev.includes(cardId)
        ? prev.filter((id) => id !== cardId)
        : [...prev, cardId],
    );
  };

  const isMaarif = (data: FormData): data is MaarifFormData => {
    return "crossProgramComponents" in data;
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <BasicInfoStep
            isMaarif={type === "maarif"}
            courseType={planData.courseType}
            grade={planData.grade}
            onLessonChange={(value) =>
              setPlanData((prev) => ({
                ...prev,
                courseType: value,
              }))
            }
            onGradeLevelChange={(value) => {
              const availableCourses = Object.entries(CourseData).filter(
                ([, course]) => course.visible(value),
              );
              const firstAvailableCourse =
                availableCourses[0]?.[0] as GetTopicsCourseType;
              const doesPreviousCourseExist = availableCourses.some(
                ([key]) => key === planData.courseType,
              );
              setPlanData((prev) => ({
                ...prev,
                grade: value,
                courseType: doesPreviousCourseExist
                  ? planData.courseType
                  : firstAvailableCourse,
              }));
            }}
          />
        );
      case 1:
        return (
          <LearningOutcomesStep
            gradeLevel={planData.grade}
            lesson={planData.courseType}
            selectedOutcomes={planData.learningOutcomes}
            isMaarif={type === "maarif"}
            onOutcomesChange={(outcomes) =>
              setPlanData((prev) => ({
                ...prev,
                learningOutcomes: outcomes,
              }))
            }
          />
        );
      case 2:
        if (type === "maarif" && isMaarif(planData)) {
          return (
            <CrossProgramStep
              learningOutcomes={planData.learningOutcomes}
              grade={planData.grade}
              courseType={planData.courseType as GetTopicsCourseType}
              customInput={planData.crossProgramComponents.customInput}
              onCustomInputChange={(value: string) =>
                setPlanData((prev) => {
                  if (isMaarif(prev)) {
                    return {
                      ...prev,
                      crossProgramComponents: {
                        ...prev.crossProgramComponents,
                        customInput: value,
                      },
                    };
                  }
                  return prev;
                })
              }
            />
          );
        }
        return (
          <LearningMethodsStep
            selectedMethods={planData.learningMethods.selected}
            onMethodsChange={(selected) =>
              setPlanData((prev) => ({
                ...prev,
                learningMethods: {
                  ...prev.learningMethods,
                  selected,
                },
              }))
            }
            teachingStyle={planData.learningMethods.teachingStyle}
            onTeachingStyleChange={(value) =>
              setPlanData((prev) => ({
                ...prev,
                learningMethods: {
                  ...prev.learningMethods,
                  teachingStyle: value,
                },
              }))
            }
          />
        );
      case 3:
        if (type === "maarif") {
          return (
            <LearningMethodsStep
              selectedMethods={planData.learningMethods.selected}
              onMethodsChange={(selected) =>
                setPlanData((prev) => ({
                  ...prev,
                  learningMethods: {
                    ...prev.learningMethods,
                    selected,
                  },
                }))
              }
              teachingStyle={planData.learningMethods.teachingStyle}
              onTeachingStyleChange={(value) =>
                setPlanData((prev) => ({
                  ...prev,
                  learningMethods: {
                    ...prev.learningMethods,
                    teachingStyle: value,
                  },
                }))
              }
            />
          );
        }
        return (
          <AdditionalInstructionsStep
            additionalInstructions={planData.additionalInstructions || ""}
            additionalArrangements={planData.additionalArrangements}
            onAdditionalInstructionsChange={(value: string) =>
              setPlanData((prev) => ({
                ...prev,
                additionalInstructions: value,
              }))
            }
            onArrangementsChange={(arrangements) =>
              setPlanData((prev) => ({
                ...prev,
                additionalArrangements: arrangements,
              }))
            }
          />
        );
      case 4:
        if (type === "maarif") {
          return (
            <AdditionalInstructionsStep
              additionalInstructions={planData.additionalInstructions || ""}
              additionalArrangements={planData.additionalArrangements}
              onAdditionalInstructionsChange={(value: string) =>
                setPlanData((prev) => ({
                  ...prev,
                  additionalInstructions: value,
                }))
              }
              onArrangementsChange={(arrangements) =>
                setPlanData((prev) => ({
                  ...prev,
                  additionalArrangements: arrangements,
                }))
              }
            />
          );
        }
        return null;
      default:
        return null;
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 0:
        return planData.courseType !== "" && planData.grade !== 0;
      case 1:
        return planData.learningOutcomes.length > 0;
      case 2:
        return true;
      case 3:
        if (type === "maarif") {
          return (planData.learningMethods.selected?.length || 0) > 0;
        }
        return true;
      case 4:
        return true;
      default:
        return false;
    }
  };

  // Show loading with steps if we're in the loading then summary flow
  if (showLoadingThenSummary) {
    return (
      <div className="max-w-6xl mx-auto py-8">
        <div className="bg-white dark:bg-gray-900 shadow-lg rounded-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/20 px-4 py-6">
            <Text
              variant="h1"
              className="!text-2xl !font-bold !text-emerald-800 dark:!text-emerald-200 text-center"
            >
              {t("dailyLessonPlan.loading.generatingPlan")}
            </Text>
          </div>

          {/* Loading Steps */}
          <div className="px-4 py-6">
            <LoadingContainer
              showSteps={loadingSteps}
              title={t("dailyLessonPlan.loading.generatingPlan")}
              themeColor="emerald"
              duration={30}
            />
          </div>

          {/* Summary Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-6">
            <div className="flex items-center justify-center mb-4">
              <Text
                variant="h3"
                className="!text-lg !font-semibold !text-gray-900 dark:!text-white"
              >
                📋 {t("dailyLessonPlan.summary.title")}
              </Text>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 max-w-4xl mx-auto">
              {/* Basic Info */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <div className="p-1.5 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 mr-3">
                    <BookOpenIcon className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    {t("dailyLessonPlan.steps.basicInfo")}
                  </h4>
                </div>
                <div className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                  <div>
                    <span className="font-medium">
                      {t("dailyLessonPlan.lesson")}:
                    </span>{" "}
                    {t(
                      `courseType.${CourseData[
                        planData.courseType as keyof typeof CourseData
                      ]?.name(planData.grade ?? 8)}`,
                    )}
                  </div>
                  <div>
                    <span className="font-medium">
                      {t("dailyLessonPlan.gradeLevel")}:
                    </span>{" "}
                    {planData.grade === -3
                      ? t("courseType.MONTH_36_48")
                      : planData.grade === -2
                        ? t("courseType.MONTH_48_60")
                        : t("dailyLessonPlan.grade", { grade: planData.grade })}
                  </div>
                </div>
              </div>

              {/* Learning Outcomes */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <div className="p-1.5 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 mr-3">
                    <AcademicCapIcon className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    {t("dailyLessonPlan.steps.outcomes")}
                  </h4>
                </div>
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  <div className="max-h-20 overflow-y-auto">
                    {planData.learningOutcomes
                      .slice(0, 3)
                      .map((outcome, index) => (
                        <div key={outcome.id} className="mb-1">
                          • {outcome.text}
                        </div>
                      ))}
                    {planData.learningOutcomes.length > 3 && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        +{planData.learningOutcomes.length - 3}{" "}
                        {t("common.more")}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Learning Methods */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <div className="p-1.5 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 mr-3">
                    <PuzzlePieceIcon className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    {t("dailyLessonPlan.steps.learningMethods")}
                  </h4>
                </div>
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  <div className="max-h-20 overflow-y-auto">
                    {planData.learningMethods.selected
                      ?.slice(0, 3)
                      .map((method) => (
                        <div key={method} className="mb-1">
                          • {t(`learningMethods.${method}.title`)}
                        </div>
                      ))}
                    {(planData.learningMethods.selected?.length || 0) > 3 && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        +{(planData.learningMethods.selected?.length || 0) - 3}{" "}
                        {t("common.more")}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Cross Program Components (Maarif only) */}
              {type === "maarif" &&
                isMaarif(planData) &&
                planData.crossProgramComponents?.selected.length > 0 && (
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                    <div className="flex items-center mb-3">
                      <div className="p-1.5 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 mr-3">
                        <ArrowPathRoundedSquareIcon className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                      </div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                        {t("dailyLessonPlan.steps.crossProgram")}
                      </h4>
                    </div>
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      <div className="max-h-20 overflow-y-auto">
                        {planData.crossProgramComponents.selected
                          .slice(0, 3)
                          .map((component, index) => (
                            <div key={index} className="mb-1">
                              • {component}
                            </div>
                          ))}
                        {planData.crossProgramComponents.selected.length >
                          3 && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            +
                            {planData.crossProgramComponents.selected.length -
                              3}{" "}
                            {t("common.more")}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

              {/* Additional Instructions */}
              {planData.additionalInstructions && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center mb-3">
                    <div className="p-1.5 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 mr-3">
                      <PencilSquareIcon className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                      {t("dailyLessonPlan.steps.additional")}
                    </h4>
                  </div>
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    <div className="max-h-16 overflow-y-auto">
                      {planData.additionalInstructions.length > 100
                        ? `${planData.additionalInstructions.substring(0, 100)}...`
                        : planData.additionalInstructions}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoadingPlan) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 border-4 border-emerald-600 dark:border-emerald-400 border-t-transparent dark:border-t-transparent rounded-full animate-spin mx-auto"></div>
          <h3 className="text-xl font-medium text-gray-900 dark:text-white">
            {t("dailyLessonPlan.loading.generatingPlan")}
          </h3>
          <Text variant="p" status="secondary">
            {t("dailyLessonPlan.loading.pleaseWait")}
          </Text>
        </div>
      </div>
    );
  }

  if (generatedPlan) {
    return (
      <LessonPlanOutput
        isLoading={false}
        data={generatedPlan || undefined}
        planType={type === "daily" ? "DAILY" : "MAARIF"}
        onBack={() => {
          setGeneratedPlan(null);
        }}
      />
    );
  }

  if (showSummary) {
    return (
      <LessonPlanSummary
        formData={planData}
        planType={type === "daily" ? "DAILY" : "MAARIF"}
        onConfirm={(response) => {
          if (response.id) {
            setIsLoadingPlan(true);
            setLessonPlanId(response.id.toString());
          }
          sendGenerateEvent();
          handleSummaryConfirm();
        }}
        onEdit={handleSummaryEdit}
      />
    );
  }

  if (!isFormComplete) {
    return (
      <div className="flex-1 lg:mt-5 bg-gray-50 dark:bg-dark-500 lg:rounded-tl-3xl overflow-y-auto h-full custom-scrollbar">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl mx-auto py-8">
            <div className="bg-white dark:bg-gray-900 shadow-lg rounded-2xl overflow-hidden p-8">
              {/* Progress Header */}
              <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/20 px-4 py-6 rounded-xl mb-6">
                <div className="flex items-center justify-between mb-4">
                  <Text
                    variant="h1"
                    className="!text-2xl !font-bold !text-emerald-800 dark:!text-emerald-200"
                  >
                    {type === "daily"
                      ? t("dailyLessonPlan.title")
                      : t("dailyLessonPlan.maarifTitle")}
                  </Text>
                  <div className="w-10 text-sm text-emerald-600 dark:text-emerald-400 font-medium">
                    {currentStep + 1} / {steps.length}
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-emerald-200 dark:bg-emerald-800 rounded-full h-2">
                  <div
                    className="bg-emerald-600 dark:bg-emerald-400 h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${((currentStep + 1) / steps.length) * 100}%`,
                    }}
                  ></div>
                </div>

                {/* Step Indicators */}
                <div className="flex items-center justify-between mt-4">
                  {steps.map((step, index) => (
                    <div key={step.id} className="flex items-center">
                      <div
                        className={`
                        flex items-center justify-center w-10 h-10 rounded-full transition-all duration-200
                        ${
                          index <= currentStep
                            ? "bg-emerald-600 text-white"
                            : "bg-emerald-200 dark:bg-emerald-800 text-emerald-600 dark:text-emerald-400"
                        }
                      `}
                      >
                        <step.icon className="w-5 h-5" />
                      </div>
                      <span
                        className={`
                        ml-2 text-sm font-medium transition-colors duration-200 hidden md:block
                        ${
                          index <= currentStep
                            ? "text-emerald-700 dark:text-emerald-300"
                            : "text-emerald-500 dark:text-emerald-600"
                        }
                      `}
                      >
                        {step.title}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Content */}
              <div className="py-8">{renderStepContent()}</div>

              {/* Footer */}
              <div className="bg-gray-50 dark:bg-gray-800 px-4 py-6 flex justify-between items-center rounded-xl mt-6">
                <button
                  onClick={() => setCurrentStep((prev) => prev - 1)}
                  disabled={currentStep === 0}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t("common.back")}
                </button>

                <button
                  onClick={handleSubmit}
                  disabled={!isStepValid()}
                  className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 dark:bg-emerald-500 border border-transparent rounded-md shadow-sm hover:bg-emerald-700 dark:hover:bg-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {currentStep === steps.length - 1
                    ? t("common.finish")
                    : t("common.next")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main lesson planning interface
  return (
    <div className="max-w-7xl mx-auto px-4 pb-8">
      <div className="">
        {/* Left Side - Activity Pool */}
        <div className="w-[calc(100%-384px)] space-y-4 md:space-y-6">
          <div className="flex items-center justify-between mb-4 md:mb-6">
            <Text
              variant="h2"
              className="!text-xl md:!text-2xl !font-bold !text-gray-900 dark:!text-white"
            >
              🎯 {t("lessonPlanner.availableActivities")}
            </Text>
            <div className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
              {t("lessonPlanner.clickToExpand")}
            </div>
          </div>

          {segmentNamesInOrder.map((sectionName, index) => {
            const SectionIcon =
              sectionIcons[sectionName as keyof typeof sectionIcons];
            const colors =
              sectionColors[sectionName as keyof typeof sectionColors];
            const items = planData.sections[sectionName] || [];
            const isExpanded = expandedSections.includes(sectionName);

            return (
              <div
                key={sectionName}
                className={`
                  bg-white dark:bg-gray-900 rounded-xl shadow-lg transition-all duration-300 
                  hover:shadow-xl transform hover:-translate-y-1
                  ${isExpanded ? "scale-[1.02]" : ""}
                `}
              >
                <div
                  className={`
                    flex items-center justify-between p-4 md:p-6 cursor-pointer transition-all duration-300
                    bg-gradient-to-r ${colors.light} dark:bg-gradient-to-r dark:${colors.dark}
                    ${colors.hover} ${isExpanded ? "rounded-t-xl" : "rounded-xl"}
                  `}
                  onClick={() => toggleSection(sectionName)}
                >
                  <div className="flex items-center space-x-3 md:space-x-4">
                    <div
                      className={`
                      w-12 h-12 md:w-14 md:h-14 bg-gradient-to-r ${colors.primary} rounded-xl 
                      flex items-center justify-center shadow-lg transform transition-transform duration-300
                      ${isExpanded ? "scale-110 rotate-6" : ""}
                    `}
                    >
                      <SectionIcon className="w-6 h-6 md:w-7 md:h-7 text-white" />
                    </div>
                    <div>
                      <Text
                        variant="h3"
                        className={`!text-lg md:!text-xl !font-bold ${colors.text}`}
                      >
                        {t(
                          `dailyLessonPlan.output.sections.${sectionName.toLowerCase()}`,
                        )}
                      </Text>
                      <div className="flex items-center mt-1 space-x-3">
                        <Text variant="span" size="sm" status="secondary">
                          {t("lessonPlanner.activitiesAvailable", {
                            count: items.length,
                          })}
                        </Text>
                        <div
                          className={`
                          px-3 py-1 rounded-full text-xs font-semibold
                          ${colors.bg} ${colors.text}
                        `}
                        >
                          {t("lessonPlanner.step", { number: index + 1 })}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div
                      className={`
                      p-2 rounded-lg transition-transform duration-300
                      ${isExpanded ? "rotate-180" : ""}
                    `}
                    >
                      <ChevronDownIcon className={`w-6 h-6 ${colors.text}`} />
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div
                    className={`
                    border-t-2 ${colors.border} p-4 md:p-6 space-y-4 
                    bg-gradient-to-b ${colors.light} dark:bg-gradient-to-b dark:${colors.dark}
                    rounded-b-xl animate-fadeIn
                  `}
                  >
                    {items.length === 0 ? (
                      <div className="text-center py-12">
                        <div
                          className={`w-20 h-20 mx-auto mb-4 rounded-full ${colors.bg} flex items-center justify-center`}
                        >
                          <SectionIcon
                            className={`w-10 h-10 ${colors.text} opacity-50`}
                          />
                        </div>
                        <Text
                          variant="p"
                          status="secondary"
                          className="text-lg"
                        >
                          {t("lessonPlanner.noActivitiesYet")}
                        </Text>
                        <Text
                          variant="p"
                          size="sm"
                          status="secondary"
                          className="mt-1"
                        >
                          {t("lessonPlanner.generateActivitiesBelow")}
                        </Text>
                      </div>
                    ) : (
                      <div className="grid gap-4">
                        {items.map((item, itemIndex) => {
                          const isEditing =
                            editingCard === `${sectionName}-${item.id}`;

                          return (
                            <div
                              key={item.id}
                              className={`
                                bg-white dark:bg-gray-900 rounded-lg p-3 md:p-5 border-2 ${colors.border}
                                shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02]
                                animate-slideInUp
                              `}
                              style={{ animationDelay: `${itemIndex * 100}ms` }}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center mb-3">
                                    <div
                                      className={`
                                      w-8 h-8 rounded-full bg-gradient-to-r ${colors.primary} 
                                      flex items-center justify-center text-white text-sm font-bold mr-3
                                    `}
                                    >
                                      {itemIndex + 1}
                                    </div>
                                    {isEditing ? (
                                      <input
                                        type="text"
                                        value={editingData?.name || ""}
                                        onChange={(e) =>
                                          setEditingData((prev) => ({
                                            ...prev!,
                                            name: e.target.value,
                                          }))
                                        }
                                        className="flex-1 text-lg font-semibold text-gray-900 dark:text-white bg-transparent border-b-2 border-gray-300 dark:border-gray-600 focus:border-emerald-500 dark:focus:border-emerald-400 outline-none"
                                        placeholder="Activity name..."
                                      />
                                    ) : (
                                      <Text
                                        variant="h4"
                                        className="!text-lg !font-semibold !text-gray-900 dark:!text-white"
                                      >
                                        {item.name}
                                      </Text>
                                    )}
                                  </div>
                                  {isEditing ? (
                                    <div className="space-y-3">
                                      <textarea
                                        value={editingData?.details || ""}
                                        onChange={(e) =>
                                          setEditingData((prev) => ({
                                            ...prev!,
                                            details: e.target.value,
                                          }))
                                        }
                                        rows={3}
                                        className="w-full text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg p-3 focus:border-emerald-500 dark:focus:border-emerald-400 focus:ring-2 focus:ring-emerald-500/20 outline-none resize-none"
                                        placeholder="Activity details..."
                                      />
                                      <div className="flex items-center space-x-2">
                                        <ClockIcon className="w-5 h-5 text-gray-400" />
                                        <input
                                          type="number"
                                          value={editingData?.duration || 0}
                                          onChange={(e) =>
                                            setEditingData((prev) => ({
                                              ...prev!,
                                              duration:
                                                parseInt(e.target.value) || 0,
                                            }))
                                          }
                                          min="1"
                                          max="120"
                                          className="w-20 text-sm font-medium bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1 focus:border-emerald-500 dark:focus:border-emerald-400 focus:ring-2 focus:ring-emerald-500/20 outline-none"
                                        />
                                        <span className="text-sm text-gray-500 dark:text-gray-400">
                                          {t("lessonPlanner.minutesLabel")}
                                        </span>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      <Text
                                        variant="p"
                                        size="sm"
                                        status="secondary"
                                        className="leading-relaxed mb-3"
                                      >
                                        {item.details}
                                      </Text>
                                      <div className="flex items-center">
                                        <ClockIcon className="w-5 h-5 text-gray-400 mr-2" />
                                        <Text
                                          variant="span"
                                          size="sm"
                                          className={`font-medium ${colors.text}`}
                                        >
                                          {t("lessonPlanner.minutes", {
                                            duration: item.duration,
                                          })}
                                        </Text>
                                      </div>
                                    </>
                                  )}
                                </div>
                                <div className="flex items-center flex-col">
                                  {isEditing ? (
                                    <>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() =>
                                          handleSaveEdit(sectionName, item.id)
                                        }
                                        className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 p-2 rounded-lg"
                                      >
                                        <svg
                                          className="w-5 h-5"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M5 13l4 4L19 7"
                                          />
                                        </svg>
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleCancelEdit}
                                        className="text-gray-500 hover:text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 p-2 rounded-lg"
                                      >
                                        <svg
                                          className="w-5 h-5"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M6 18L18 6M6 6l12 12"
                                          />
                                        </svg>
                                      </Button>
                                    </>
                                  ) : (
                                    <>
                                      <Button
                                        variant="solid"
                                        size="lg"
                                        onClick={() =>
                                          handleEditCard(sectionName, item)
                                        }
                                        className={`
                                          shadow-lg 
                                          transition-all duration-300 hover:shadow-xl 
                                          transform hover:scale-110
                                          px-4 py-3 rounded-xl
                                          bg-gray-100 dark:bg-gray-700 mb-2
                                        `}
                                      >
                                        <PencilIcon className="w-5 h-5" />
                                      </Button>
                                      <Button
                                        variant="solid"
                                        size="lg"
                                        onClick={() =>
                                          handleAddToFinalPlan(
                                            sectionName,
                                            item,
                                          )
                                        }
                                        className={`
                                          ${colors.button} text-white shadow-lg 
                                          transition-all duration-300 hover:shadow-xl 
                                          transform hover:scale-110 ${colors.ring}
                                          px-4 py-3 rounded-xl
                                        `}
                                      >
                                        <PlusIcon className="w-5 h-5" />
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Generate More Button - placed at the bottom of the expanded section */}
                    <div className="flex justify-center pt-4 mt-6 border-t-2 border-gray-200 dark:border-gray-600">
                      <Button
                        variant="solid"
                        size="md"
                        onClick={() => handleGenerateExtraCard(sectionName)}
                        disabled={!!isGeneratingExtraCard || isGenerating}
                        className={`${colors.button} ${colors.ring} text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:transform-none px-6 py-3 rounded-lg font-medium`}
                      >
                        {isGeneratingExtraCard === sectionName ? (
                          <div className="flex items-center">
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                            <span>
                              {t("lessonPlanner.generatingExtraCard")}
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <PlusIcon className="w-5 h-5 mr-2" />
                            <span>{t("lessonPlanner.generateExtraCard")}</span>
                          </div>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Right Side - Final Plan */}
        <div className="space-y-4 md:space-y-6 fixed right-0 top-0 w-96">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl border-2 border-gray-200 dark:border-gray-700 sticky top-4 overflow-hidden">
            <div className="p-4 md:p-6 md:py-4 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 border-b-2 border-gray-200 dark:border-gray-600">
              <div className="flex items-center justify-between">
                <Text
                  variant="h2"
                  className="!text-lg md:!text-2xl !font-bold !text-gray-900 dark:!text-white flex items-center"
                >
                  📋 {t("lessonPlanner.yourLessonPlan")}
                </Text>
                <div className="text-right">
                  <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                    {finalPlan.reduce((acc, item) => acc + item.duration, 0)}{" "}
                    {t("lessonPlanner.minShort")}
                  </div>
                  <Text variant="span" size="xs" status="secondary">
                    {t("lessonPlanner.totalDuration")}
                  </Text>
                </div>
              </div>
              {/* <Text
                variant="p"
                size="sm"
                status="secondary"
                className="flex items-center"
              >
                <span className="mr-2">🎲</span>
                {t("lessonPlanner.activitiesSelected", {
                  count: finalPlan.length,
                })}
              </Text> */}
            </div>

            <div
              ref={finalPlanContainerRef}
              className="p-4 h-[calc(100vh-178px)] overflow-y-auto custom-scrollbar"
            >
              {finalPlan.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 flex items-center justify-center">
                    <BookOpenIcon className="w-12 h-12 text-gray-400 dark:text-gray-500" />
                  </div>
                  <Text
                    variant="p"
                    className="text-lg font-medium text-gray-600 dark:text-gray-300 mb-2"
                  >
                    {t("lessonPlanner.readyToBuild")}
                  </Text>
                  <Text variant="p" size="sm" status="secondary">
                    {t("lessonPlanner.addActivitiesInstruction")}
                  </Text>
                </div>
              ) : (
                <div className="space-y-4">
                  {segmentNamesInOrder.map((sectionName) => {
                    const sectionItems = finalPlan.filter(
                      (item) => item.segmentName === sectionName,
                    );
                    const colors =
                      sectionColors[sectionName as keyof typeof sectionColors];
                    if (sectionItems.length === 0) return null;

                    return (
                      <div key={sectionName} className="animate-slideInUp">
                        <div className="flex items-center mb-3">
                          <div
                            className={`w-4 h-4 rounded-full bg-gradient-to-r ${colors.primary} mr-3`}
                          ></div>
                          <Text
                            variant="h4"
                            className={`!text-sm !font-bold uppercase tracking-wider ${colors.text}`}
                          >
                            {t(
                              `dailyLessonPlan.output.sections.${sectionName.toLowerCase()}`,
                            )}
                          </Text>
                          <div
                            className={`ml-auto px-2 py-1 rounded-full text-xs ${colors.bg} ${colors.text}`}
                          >
                            {sectionItems.reduce(
                              (acc, item) => acc + item.duration,
                              0,
                            )}{" "}
                            {t("lessonPlanner.minShort")}
                          </div>
                        </div>
                        {sectionItems.map((item, index) => {
                          const isEditing = editingCard === `final-${item.id}`;
                          const isFinalCardExpanded =
                            expandedFinalCards.includes(item.id);

                          return (
                            <div
                              key={item.id}
                              ref={(el) =>
                                (finalPlanItemRefs.current[item.id] = el)
                              }
                              className={`
                                bg-gradient-to-r ${colors.light} dark:bg-gradient-to-r dark:${colors.dark}
                                rounded-lg p-4 pr-2 mb-3 border-2 ${colors.border}
                                shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02]
                                group
                              `}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-3 mb-2">
                                    {isEditing ? (
                                      <input
                                        type="text"
                                        value={editingData?.name || ""}
                                        onChange={(e) =>
                                          setEditingData((prev) => ({
                                            ...prev!,
                                            name: e.target.value,
                                          }))
                                        }
                                        className="flex-1 text-base font-semibold text-gray-900 dark:text-white bg-transparent border-b-2 border-gray-300 dark:border-gray-600 focus:border-emerald-500 dark:focus:border-emerald-400 outline-none"
                                        placeholder="Activity name..."
                                      />
                                    ) : (
                                      <Text
                                        variant="h5"
                                        className="!text-base !font-semibold !text-gray-900 dark:!text-white flex-grow"
                                      >
                                        {item.name}
                                      </Text>
                                    )}
                                  </div>
                                  {isEditing ? (
                                    <div className="space-y-3">
                                      <textarea
                                        value={editingData?.details || ""}
                                        onChange={(e) =>
                                          setEditingData((prev) => ({
                                            ...prev!,
                                            details: e.target.value,
                                          }))
                                        }
                                        rows={2}
                                        className="w-full text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg p-3 focus:border-emerald-500 dark:focus:border-emerald-400 focus:ring-2 focus:ring-emerald-500/20 outline-none resize-none"
                                        placeholder="Activity details..."
                                      />
                                      <div className="flex items-center space-x-2">
                                        <ClockIcon className="w-4 h-4 text-gray-400" />
                                        <input
                                          type="number"
                                          value={editingData?.duration || 0}
                                          onChange={(e) =>
                                            setEditingData((prev) => ({
                                              ...prev!,
                                              duration:
                                                parseInt(e.target.value) || 0,
                                            }))
                                          }
                                          min="1"
                                          max="120"
                                          className="w-20 text-sm font-medium bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1 focus:border-emerald-500 dark:focus:border-emerald-400 focus:ring-2 focus:ring-emerald-500/20 outline-none"
                                        />
                                        <span className="text-sm text-gray-500 dark:text-gray-400">
                                          {t("lessonPlanner.minutesLabel")}
                                        </span>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      {isFinalCardExpanded && (
                                        <Text
                                          variant="p"
                                          size="sm"
                                          status="secondary"
                                          className="leading-relaxed mb-2"
                                        >
                                          {item.details}
                                        </Text>
                                      )}
                                      <div className="flex items-center mt-2">
                                        {!isFinalCardExpanded &&
                                          item.details && (
                                            <Text
                                              variant="p"
                                              size="xs"
                                              status="secondary"
                                              className="leading-relaxed line-clamp-2 flex-grow mr-2"
                                            >
                                              {item.details}
                                            </Text>
                                          )}
                                        <ClockIcon className="w-4 h-4 text-gray-400 mr-2" />
                                        {isFinalCardExpanded && (
                                          <Text
                                            variant="span"
                                            size="sm"
                                            className={`font-medium ${colors.text}`}
                                          >
                                            {t("lessonPlanner.minutes", {
                                              duration: item.duration,
                                            })}
                                          </Text>
                                        )}
                                      </div>
                                    </>
                                  )}
                                </div>
                                <div className="flex flex-col items-center ml-2 space-y-1">
                                  {isEditing ? (
                                    <>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() =>
                                          handleSaveFinalEdit(item.id)
                                        }
                                        className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 p-2 rounded-lg"
                                      >
                                        <svg
                                          className="w-4 h-4"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M5 13l4 4L19 7"
                                          />
                                        </svg>
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleCancelEdit}
                                        className="text-gray-500 hover:text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 p-2 rounded-lg"
                                      >
                                        <svg
                                          className="w-4 h-4"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M6 18L18 6M6 6l12 12"
                                          />
                                        </svg>
                                      </Button>
                                    </>
                                  ) : (
                                    <>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => toggleFinalCard(item.id)}
                                        className="text-gray-500 hover:text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 p-2 rounded-lg"
                                      >
                                        <ChevronDownIcon
                                          className={`w-5 h-5 transition-transform duration-200 ${
                                            isFinalCardExpanded
                                              ? "transform rotate-180"
                                              : ""
                                          }`}
                                        />
                                      </Button>
                                      {isFinalCardExpanded && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() =>
                                            handleEditFinalCard(item)
                                          }
                                          className="text-gray-500 hover:text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 p-2 rounded-lg"
                                        >
                                          <PencilIcon className="w-4 h-4" />
                                        </Button>
                                      )}
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() =>
                                          handleRemoveFromFinalPlan(item)
                                        }
                                        className={`
                                          text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20
                                          p-2 rounded-lg transition-all duration-300 transform hover:scale-110
                                          ${!isFinalCardExpanded ? "opacity-100" : "opacity-0 group-hover:opacity-100"}
                                        `}
                                      >
                                        <TrashIcon className="w-4 h-4" />
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="p-6 py-3 bg-gradient-to-r from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/20 border-t-2 border-emerald-200 dark:border-emerald-700">
              <Button
                variant="solid"
                onClick={handleGeneratePlan}
                disabled={finalPlan.length === 0 || isGenerating}
                className="w-full bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white py-4 text-lg font-bold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02] rounded-xl disabled:opacity-50 disabled:transform-none disabled:shadow-lg focus:ring-4 focus:ring-emerald-500/50"
              >
                {isGenerating ? (
                  <div className="flex items-center justify-center">
                    <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                    <span>{t("lessonPlanner.generating")}</span>
                  </div>
                ) : (
                  t("dailyLessonPlan.actions.generatePlan")
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LessonPlanForm;

{
  /* Add custom CSS animations */
}
<style>{`
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .animate-fadeIn {
    animation: fadeIn 0.3s ease-out;
  }

  .animate-slideInUp {
    animation: slideInUp 0.5s ease-out;
  }

  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
  }

  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }
`}</style>;
